import {isUndefined} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InterfaceEventsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {defaultEmptyStateRenderer} from '../../reportElements/charts/emptyState';
import {ReportSection} from '../../reportElements/reportSection/reportSection';
import {TableColumnPickerWithNames} from '../../reportElements/table/tableColumnPickerWithNames';
import {AnalyticsEducationTooltip} from '../../reportElements/tooltips/analyticsTooltips';
import {Table} from '../../table/table';
import {LIVE_DASHBOARD_HELP_CENTER_URL} from '../constants';
import {
  liveDashboardConversationColumnsDefaultHiddenIds,
  LiveDashboardHideableConversationColumnId,
  liveDashboardHideableConversationColumnIds,
  liveDashboardHideableConversationColumnNames,
  makeLiveDashboardOldestConversationsColumns,
} from './liveDashboardOldestOpenConversationsColumns';

/*
 * Props.
 */

interface LiveDashboardOldestOpenConversationsProps {
  panelId: AnalyticsPanelId;
  oldestOpenConversationIds: ReadonlyArray<number> | undefined;
  isThisPoppedOut: boolean;
  hasWaitingMetrics: boolean;
}

/*
 * Styles.
 */

const SyledTableWrapperDiv = styled.div`
  & tbody {
    font-size: ${fontSizes.medium};
  }
`;

/*
 * Component.
 */

// TODO(ldemarne): Remove `Open` from name.
export const LiveDashboardOldestOpenConversations: FC<LiveDashboardOldestOpenConversationsProps> = ({
  panelId,
  oldestOpenConversationIds,
  isThisPoppedOut,
  hasWaitingMetrics,
}) => {
  const rows =
    oldestOpenConversationIds?.map((id) => ({
      conversation_id: id,
    })) ?? [];

  const getPanelId = useMemo(() => () => panelId, [panelId]);
  const trackAnalytics = useAnalyticsTracking(getPanelId);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Array<LiveDashboardHideableConversationColumnId>>(
    liveDashboardConversationColumnsDefaultHiddenIds,
  );

  const tooltip = hasWaitingMetrics ? sectionTooltipWithWaiting : sectionTooltip;
  const columns = useMemo(
    () =>
      makeLiveDashboardOldestConversationsColumns({isPoppedOut: isThisPoppedOut, hasWaitingMetrics, panelId}),
    [hasWaitingMetrics, isThisPoppedOut, panelId],
  );

  const renderColumnPicker = () => (
    <TableColumnPickerWithNames
      isLoading={false}
      disabledColumnIds={[]}
      canBeHiddenColumnIds={liveDashboardHideableConversationColumnIds}
      columnNames={liveDashboardHideableConversationColumnNames}
      hiddenColumnIds={hiddenColumnIds}
      onColumnClick={(columnId) => {
        const isCurrentlyHidden = hiddenColumnIds.includes(columnId);

        if (isCurrentlyHidden) {
          setHiddenColumnIds(hiddenColumnIds.filter((id) => id !== columnId));
        } else {
          setHiddenColumnIds([...hiddenColumnIds, columnId]);
        }

        trackAnalytics(InterfaceEventsEnum.ANALYTICS_COLUMN_LIST_EDIT, {
          report_section: ReportElementIdsEnum.LIVE_DASHBOARD_OLDEST_OPEN_CONVERSATIONS_TABLE,
          metric_type: columnId,
          selection_type: isCurrentlyHidden ? 'add' : 'remove',
        });
      }}
    />
  );

  return (
    <ReportSection
      isDisabled={false}
      reportSectionId={ReportElementIdsEnum.LIVE_DASHBOARD_OLDEST_OPEN_CONVERSATIONS_TABLE}
      title={tooltip.title}
      sectionTooltip={tooltip}
      customContentRenderer={(_) => renderColumnPicker()}
    >
      <SyledTableWrapperDiv>
        <Table
          isLoading={isUndefined(oldestOpenConversationIds)}
          isEmpty={oldestOpenConversationIds?.length === 0}
          columns={columns}
          hiddenColumnIds={hiddenColumnIds}
          data={rows}
          renderEmptyState={defaultEmptyStateRenderer}
          disableSelection
          disableGlobalFilter
          disableTablePagination
          displayTableTitleBar={false}
          disableSortBy
          shouldHideBottomBorder
        />
      </SyledTableWrapperDiv>
    </ReportSection>
  );
};

/*
 * Constants.
 */

const sectionTooltip: AnalyticsEducationTooltip = {
  title: <Trans id="live-dashboard-oldest-open-conversations">Oldest open conversations</Trans>,
  tooltipContent: (
    <Trans id="analytics-live-oldest-open-conversations-tooltip-description">
      Identify top 5 open conversation with no messages from the longest time. Conversations in this list
      should be treated in priority by replying, archiving, or snoozing.
    </Trans>
  ),
  learnMoreUrl: `${LIVE_DASHBOARD_HELP_CENTER_URL}#oldest_open_conversations_table`,
};

const sectionTooltipWithWaiting: AnalyticsEducationTooltip = {
  // TODO(ldemarne): Figure out if we should show this title regardless of the filter value.
  title: <Trans id="live-dashboard-oldest-open-conversations">Oldest conversations</Trans>,
  tooltipContent: (
    <Trans id="analytics-live-oldest-open-conversations-tooltip-description">
      Identify top 5 open or waiting conversations with no messages from the longest time. Conversations in
      this list should be treated in priority by replying, archiving, or snoozing.
    </Trans>
  ),
  learnMoreUrl: `${LIVE_DASHBOARD_HELP_CENTER_URL}#oldest_open_conversations_table`,
};
